import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Identifies one of the two legend-driven series a chart can render.
 */
export type ChartLegendSeriesKey = 'primary' | 'comparison';

/**
 * What the user is doing with a chart's legend.
 */
export interface ChartLegendState {
  /** Legend the user clicked. Only its series is rendered. `null` is the default view (both series). */
  selected: ChartLegendSeriesKey | null;
  /** Legend currently under the mouse, if any. */
  hovered: ChartLegendSeriesKey | null;
}

export const DEFAULT_LEGEND_STATE: ChartLegendState = { selected: null, hovered: null };

/**
 * How a chart should render its primary and comparison series, derived from ChartLegendState.
 */
export interface ChartLegendVisibility {
  primaryVisible: boolean;
  comparisonVisible: boolean;
  /** 1 normally, DIMMED_SERIES_OPACITY while the other legend is hovered in the default view */
  primaryOpacity: number;
  comparisonOpacity: number;
}

export const ALL_LEGEND_SERIES_VISIBLE: ChartLegendVisibility = {
  primaryVisible: true,
  comparisonVisible: true,
  primaryOpacity: 1,
  comparisonOpacity: 1
};

/** Opacity of a legend whose series is hidden because the other legend is selected */
export const UNSELECTED_LEGEND_OPACITY = 0.4;

/** Opacity of a series while the other series' legend is hovered in the default view */
export const DIMMED_SERIES_OPACITY = 0.1;

/**
 * Opacity of a bar while the other series' legend is hovered in the default view. Bars are solid
 * fills, so they stay more visible than lines fading to DIMMED_SERIES_OPACITY.
 */
export const DIMMED_BAR_OPACITY = 0.3;

/**
 * Handles a click on a legend:
 * - In the default view, clicking a legend selects it: only its series stays rendered.
 * - Clicking the selected legend again goes back to the default view.
 * - Clicking the other legend while one is selected moves the selection to it.
 */
export const selectLegend = (
  selected: ChartLegendSeriesKey | null,
  target: ChartLegendSeriesKey
): ChartLegendSeriesKey | null => (selected === target ? null : target);

/**
 * Derives series visibility and opacity from the legend state:
 * - A selected legend hides the other series.
 * - In the default view, hovering a legend dims the other series to DIMMED_SERIES_OPACITY.
 *   Hovering while a legend is selected only affects the legend itself, never the lines.
 */
export const getLegendVisibility = ({ selected, hovered }: ChartLegendState): ChartLegendVisibility => {
  const isVisible = (key: ChartLegendSeriesKey) => selected === null || selected === key;
  const getOpacity = (key: ChartLegendSeriesKey) =>
    selected === null && hovered !== null && hovered !== key ? DIMMED_SERIES_OPACITY : 1;
  return {
    primaryVisible: isVisible('primary'),
    comparisonVisible: isVisible('comparison'),
    primaryOpacity: getOpacity('primary'),
    comparisonOpacity: getOpacity('comparison')
  };
};

/**
 * Provided per chart by BeaconChartWithLegend (and BeaconProChart). Charts read it to decide
 * which of their primary/comparison series to render and how opaque they are. Defaults to
 * everything visible so charts rendered outside a provider are unaffected.
 */
export const ChartLegendVisibilityContext = createContext<ChartLegendVisibility>(ALL_LEGEND_SERIES_VISIBLE);

export const useChartLegendVisibility = (): ChartLegendVisibility => useContext(ChartLegendVisibilityContext);

/**
 * Hook owning legend state. Returns the derived visibility for the chart, the selected legend,
 * a stable click handler and a stable hover setter.
 *
 * @param resetKey when this value changes (e.g. the chart's data, time period or metric changed)
 * the selection goes back to the default view, so a freshly loaded series is never hidden by a
 * selection made against the previous data.
 */
export const useLegendVisibilityState = (resetKey?: unknown) => {
  const [selected, setSelected] = useState<ChartLegendSeriesKey | null>(null);
  const [hovered, setHovered] = useState<ChartLegendSeriesKey | null>(null);
  const toggle = useCallback((target: ChartLegendSeriesKey) => {
    setSelected((current) => selectLegend(current, target));
  }, []);

  useEffect(() => {
    setSelected(null);
  }, [resetKey]);

  const visibility = useMemo(() => getLegendVisibility({ selected, hovered }), [selected, hovered]);

  return useMemo(() => ({ visibility, selected, toggle, setHovered }), [visibility, selected, toggle]);
};

/**
 * Styles for the element wrapping a Highcharts chart so that series opacity changes driven by
 * legend hover fade instead of snapping. Highcharts writes opacity as an SVG attribute on the
 * series groups, which a CSS transition picks up.
 */
export const CHART_SERIES_OPACITY_TRANSITION_SX = {
  '& .highcharts-series, & .highcharts-markers, & .highcharts-data-labels': {
    transition: 'opacity 200ms ease-in-out'
  }
} as const;

/**
 * Wraps a legend so it can be clicked (and keyboard-activated) to select its series, and hovered.
 * When the other legend is selected this legend is dimmed, except while hovered.
 */
export const ToggleableLegend = ({
  visible,
  onToggle,
  onHoverChange,
  children
}: {
  /** Whether this legend's series is currently rendered */
  visible: boolean;
  onToggle: () => void;
  onHoverChange?: (hovering: boolean) => void;
  children: React.ReactNode;
}) => {
  const [hovering, setHovering] = useState(false);
  const handleHover = (nextHovering: boolean) => {
    setHovering(nextHovering);
    if (onHoverChange) onHoverChange(nextHovering);
  };
  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={visible}
      onClick={onToggle}
      onMouseEnter={() => handleHover(true)}
      onMouseLeave={() => handleHover(false)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onToggle();
        }
      }}
      style={{
        cursor: 'pointer',
        opacity: visible || hovering ? 1 : UNSELECTED_LEGEND_OPACITY,
        transition: 'opacity 150ms ease-in-out',
        userSelect: 'none'
      }}
    >
      {children}
    </div>
  );
};
